import { FC } from "react";

import { Box, HStack, Text } from "@/components/ui";

import {
  NO_DATA,
  NO_DATA_STROKE,
  NO_RACE,
  RAMPS,
  STRIPE,
  TOSSUP,
} from "./colors";
import type { Layer } from "./types";
import type { CountyLayer } from "./view";

interface Item {
  color: string;
  dashed?: boolean;
  label: string;
  striped?: boolean;
}

const Swatch: FC<{ item: Item }> = ({ item }) => (
  <Box
    className="w-3.5 h-3.5 rounded-sm overflow-hidden"
    style={{
      backgroundColor: item.color,
      borderColor: NO_DATA_STROKE,
      borderStyle: item.dashed ? "dashed" : "solid",
      borderWidth: item.dashed || item.color === NO_RACE ? 1 : 0,
    }}
  >
    {item.striped &&
      [-3, 3].map((offset) => (
        <Box
          className="absolute"
          key={offset}
          style={{
            backgroundColor: STRIPE,
            height: 2,
            left: -3,
            top: 6 + offset,
            transform: [{ rotate: "-45deg" }],
            width: 20,
          }}
        />
      ))}
  </Box>
);

const Row: FC<{ items: Item[] }> = ({ items }) => (
  <HStack className="flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
    {items.map((item) => (
      <HStack className="items-center gap-1.5" key={item.label}>
        <Swatch item={item} />
        <Text className="text-xs text-typography-600">{item.label}</Text>
      </HStack>
    ))}
  </HStack>
);

const ratingItems = (showIndependent: boolean): Item[] => [
  { color: RAMPS.D.safe, label: "Safe D" },
  { color: RAMPS.D.likely, label: "Likely D" },
  { color: RAMPS.D.lean, label: "Lean D" },
  { color: TOSSUP, label: "Toss-up" },
  { color: RAMPS.R.lean, label: "Lean R" },
  { color: RAMPS.R.likely, label: "Likely R" },
  { color: RAMPS.R.safe, label: "Safe R" },
  ...(showIndependent
    ? [{ color: RAMPS.I.likely, label: "Independent ahead" }]
    : []),
];

/** Five-step diverging scale: [strong D, D, neutral, R, strong R] labels. */
const gapItems = (labels: [string, string, string, string, string]): Item[] => [
  { color: RAMPS.D.likely, label: labels[0] },
  { color: RAMPS.D.lean, label: labels[1] },
  { color: TOSSUP, label: labels[2] },
  { color: RAMPS.R.lean, label: labels[3] },
  { color: RAMPS.R.likely, label: labels[4] },
];

export const NationalLegend: FC<{
  layer: Layer;
  showIndependent: boolean;
  sourceLabel: string | null;
}> = ({ layer, showIndependent, sourceLabel }) => (
  <Row
    items={
      layer === "difference"
        ? [
            ...gapItems([
              "15+ pts more D",
              "5–15 pts more D",
              `Within 5 pts of ${sourceLabel ?? "source"}`,
              "5–15 pts more R",
              "15+ pts more R",
            ]),
            ...(showIndependent
              ? [{ color: RAMPS.I.likely, label: "More pro-independent" }]
              : []),
            { color: NO_DATA, dashed: true, label: "Missing a read" },
          ]
        : [
            ...ratingItems(showIndependent),
            { color: TOSSUP, label: "Projected flip", striped: true },
            { color: NO_DATA, dashed: true, label: "Not surveyed" },
            { color: NO_RACE, label: "No race in 2026" },
          ]
    }
  />
);

export const CountyLegend: FC<{ layer: CountyLayer }> = ({ layer }) => (
  <Row
    items={
      layer === "shift"
        ? [
            ...gapItems([
              "6+ pts to D vs 2024",
              "2–6 pts to D",
              "Within 2 pts",
              "2–6 pts to R",
              "6+ pts to R vs 2024",
            ]),
            { color: NO_DATA, dashed: true, label: "No 2024 data" },
          ]
        : [
            { color: RAMPS.D.safe, label: "D +30" },
            { color: RAMPS.D.likely, label: "D +15" },
            { color: RAMPS.D.lean, label: "D +4" },
            { color: TOSSUP, label: "Within 4" },
            { color: RAMPS.R.lean, label: "R +4" },
            { color: RAMPS.R.likely, label: "R +15" },
            { color: RAMPS.R.safe, label: "R +30" },
            ...(layer === "foresight"
              ? [{ color: NO_RACE, label: "No respondents" }]
              : []),
          ]
    }
  />
);
